import { StoryTrailState, type StoryTrailCheckResult } from '../minigames/StoryTrailState.js';
import type { StoryTrailConfig } from '../minigames/StoryTrailConfig.js';

export interface SequenceStreamSerializedState {
  readonly version: 1;
  readonly placedOrder: readonly (string | null)[];
  readonly selectedEventId: string | null;
  readonly hintStage: number;
  readonly attempts: number;
  readonly complete: boolean;
}

export class SequenceStreamState {
  private readonly trail: StoryTrailState;
  private selectedEventId: string | null = null;
  private hintStage = 0;
  private attempts = 0;
  private completed = false;

  constructor(
    readonly config: StoryTrailConfig,
    saved?: Partial<SequenceStreamSerializedState>,
  ) {
    this.trail = new StoryTrailState(config);
    this.trail.collectAll();
    if (saved?.version === 1) this.restore(saved);
  }

  select(eventId: string) {
    if (this.completed || !this.config.events.some(event => event.id === eventId)) return false;
    this.selectedEventId = this.selectedEventId === eventId ? null : eventId;
    return true;
  }

  activateSlot(slotIndex: number) {
    if (this.completed || !Number.isInteger(slotIndex) || slotIndex < 0
      || slotIndex >= this.config.events.length) return false;
    if (this.selectedEventId) {
      const changed = this.trail.place(this.selectedEventId, slotIndex);
      if (changed) this.selectedEventId = null;
      return changed;
    }
    const existing = this.trail.placedOrder[slotIndex];
    if (!existing) return false;
    this.trail.remove(slotIndex);
    this.selectedEventId = existing;
    return true;
  }

  check(): StoryTrailCheckResult {
    this.attempts += 1;
    const result = this.trail.check();
    if (result === 'correct') this.completed = true;
    return result;
  }

  requestHint() {
    this.hintStage = Math.min(3, this.hintStage + 1);
    const first = this.trail.eventForId(this.config.correctOrder[0]);
    const last = this.trail.eventForId(this.config.correctOrder.at(-1) ?? null);
    return [
      this.config.hint,
      first ? `Begin with: ${first.text}` : this.config.hint,
      last ? `End with: ${last.text}` : this.config.hint,
    ][this.hintStage - 1];
  }

  reset() {
    if (this.completed) return false;
    this.trail.resetOrder();
    this.selectedEventId = null;
    this.hintStage = 0;
    return true;
  }

  serialize(): SequenceStreamSerializedState {
    return {
      version: 1,
      placedOrder: this.trail.placedOrder,
      selectedEventId: this.selectedEventId,
      hintStage: this.hintStage,
      attempts: this.attempts,
      complete: this.completed,
    };
  }

  get snapshot() {
    return {
      ...this.serialize(),
      availableEventIds: this.config.events
        .map(event => event.id)
        .filter(id => !this.trail.placedOrder.includes(id)),
    };
  }

  eventForId(eventId: string | null) {
    return this.trail.eventForId(eventId);
  }

  private restore(saved: Partial<SequenceStreamSerializedState>) {
    const order = saved.placedOrder;
    if (Array.isArray(order) && order.length === this.config.events.length) {
      const valid = new Set(this.config.events.map(event => event.id));
      const used = new Set<string>();
      order.forEach((eventId, slotIndex) => {
        if (typeof eventId === 'string' && valid.has(eventId) && !used.has(eventId)) {
          this.trail.place(eventId, slotIndex);
          used.add(eventId);
        }
      });
    }
    this.selectedEventId = typeof saved.selectedEventId === 'string'
      && this.config.events.some(event => event.id === saved.selectedEventId)
      && !this.trail.placedOrder.includes(saved.selectedEventId)
      ? saved.selectedEventId
      : null;
    this.hintStage = Math.min(3, Math.max(0, Number(saved.hintStage) || 0));
    this.attempts = Math.max(0, Number(saved.attempts) || 0);
    this.completed = saved.complete === true && this.trail.check() === 'correct';
  }
}
